import { QueryClient } from "@tanstack/react-query";

/**
 * Default configuration for TanStack Query
 *
 * This configuration provides sensible defaults for:
 * - Stale time: How long data is considered fresh (5 minutes)
 * - Cache time: How long unused data stays in cache (10 minutes)
 * - Retry behavior: 3 attempts with exponential backoff
 * - Refetch settings: Conservative approach to reduce unnecessary requests
 */
const queryClientConfig = {
  defaultOptions: {
    queries: {
      // Data is considered fresh for 5 minutes
      staleTime: 1000 * 60 * 5,

      // Unused data stays in cache for 10 minutes
      gcTime: 1000 * 60 * 10,

      // Reduce retries for faster failure handling
      retry: 2,
      retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 10000),

      // Don't refetch on window focus by default (can be overridden per query)
      refetchOnWindowFocus: false,

      // Refetch on reconnect if data is stale
      refetchOnReconnect: true,

      // Refetch on mount if data is stale
      refetchOnMount: true,

      // Enable network mode to handle offline scenarios better
      networkMode: "online" as const,

      // Prevent blocking behavior with shorter timeouts
      throwOnError: false,
    },
    mutations: {
      // Retry failed mutations once with shorter delay
      retry: 1,
      retryDelay: 500,

      // Don't block UI for mutation errors
      throwOnError: false,
    },
  },
};

/**
 * Singleton QueryClient instance for the application
 *
 * Use this instance throughout your app to ensure consistent caching
 * and configuration across all components.
 */
export const queryClient = new QueryClient(queryClientConfig);
